import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import ActionCard from './ActionCard';

function ActionQueue({ items, filterTier, onNavigateToClient }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const filteredItems = filterTier
    ? items.filter((item) => item.tier === filterTier)
    : items;

  const checkScrollable = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkScrollable();
  }, [filteredItems, checkScrollable]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScrollable);
    window.addEventListener('resize', checkScrollable);
    return () => {
      el.removeEventListener('scroll', checkScrollable);
      window.removeEventListener('resize', checkScrollable);
    };
  }, [checkScrollable]);

  if (!filteredItems || filteredItems.length === 0) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        bgcolor: '#e8f5e9',
        borderRadius: 1,
        border: '1px solid',
        borderColor: '#c8e6c9',
      }}>
        <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
        <Typography variant="body2" color="text.secondary">
          No urgent actions — TASS is monitoring your caseload
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', minHeight: 280, maxHeight: 340 }}>
      {canScrollLeft && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 32,
            background: 'linear-gradient(to right, rgba(255,255,255,0.95), rgba(255,255,255,0))',
            zIndex: 1,
            pointerEvents: 'none'
          }}
        />
      )}
      {canScrollRight && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 32,
            background: 'linear-gradient(to left, rgba(255,255,255,0.95), rgba(255,255,255,0))',
            zIndex: 1,
            pointerEvents: 'none'
          }}
        />
      )}
      <Box
        ref={scrollRef}
        sx={{
          overflowX: 'auto',
          display: 'flex',
          gap: 2,
          py: 1,
          px: 2,
          height: '100%',
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 3
          }
        }}
      >
        {filteredItems.map((item) => (
          <ActionCard
            key={item.id}
            item={item}
            onNavigateToClient={onNavigateToClient}
          />
        ))}
      </Box>
    </Box>
  );
}

export default ActionQueue;
